import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { BillingApiError, BillingSummary, BillingService } from '../../services/billing.service';
import { User } from '../../services/auth.service';

@Component({
  selector: 'app-payment-methods-page',
  standalone: true,
  imports: [DatePipe, RouterLink, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './payment-methods-page.component.html',
  styleUrls: ['./payment-methods-page.component.scss']
})
export class PaymentMethodsPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  isInitialLoading = true;
  hasInitialLoadError = false;
  isPortalBusy = false;
  billingMessage = '';
  billingError = '';
  currentUser: User | null = null;
  billing: BillingSummary | null = null;

  constructor(private readonly billingService: BillingService) {}

  ngOnInit(): void {
    this.loadPageData();
  }

  get hasPaddleRelationship(): boolean {
    return !!this.billing?.customerId;
  }

  loadPageData(): void {
    this.isInitialLoading = true;
    this.hasInitialLoadError = false;
    this.billingError = '';

    this.billingService.getPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.currentUser = state.currentUser;
            this.billing = state.billing;
            this.isInitialLoading = false;
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.isInitialLoading = false;
            this.hasInitialLoadError = true;
          });
        }
      });
  }

  openBillingPortal(): void {
    this.billingMessage = '';
    this.billingError = '';
    this.isPortalBusy = true;

    this.billingService.createPortalSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (url) => {
          this.runInAngular(() => {
            this.isPortalBusy = false;
          });
          const portalWindow = window.open(url, '_blank', 'noopener,noreferrer');
          if (!portalWindow) {
            this.runInAngular(() => {
              this.billingError = 'The billing portal was blocked by the browser. Allow popups and try again.';
            });
          }
        },
        error: (error: BillingApiError) => {
          this.runInAngular(() => {
            this.billingError = error.message;
            this.isPortalBusy = false;
          });
        }
      });
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
